import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import CustomSelect from '../../../ui/CustomSelect';
import { parseRupiah } from '../../../hris/recruitment/utils/formatRupiah';

interface AssumptionsSectionProps {
  projection: any;
  calculated: any;
  onChange: (field: string, value: string | number) => void;
}

export default function AssumptionsSection({
  projection,
  calculated,
  onChange,
}: AssumptionsSectionProps) {
  // Format currency untuk input field
  const formatCurrencyInput = (value: any): string => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('id-ID').format(numValue);
  };

  const handleCurrencyChange = (field: string, value: string) => {
    const rawValue = parseRupiah(value);
    if (rawValue === '') {
      onChange(field, '');
      return;
    }
    const numValue = parseFloat(rawValue);
    onChange(field, isNaN(numValue) ? '' : rawValue);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Ketentuan Umum dan Asumsi Terkait Angsuran dan Biaya
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Asumsi Kemampuan Bayar dan Suku Bunga */}
          <div>
            <h4 className="font-medium mb-3">Asumsi Kemampuan Bayar dan Suku Bunga:</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Repayment Capacity Dari Net Cash Flow</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={projection.repayment_capacity_pct ?? ''}
                    onChange={e =>
                      onChange(
                        'repayment_capacity_pct',
                        e.target.value === '' ? '' : e.target.value
                      )
                    }
                    className="text-right focus:border-blue-500"
                  />
                  <span className="flex items-center">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Suku Bunga Flat per Tahun</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={projection.suku_bunga_flat ?? ''}
                    onChange={e =>
                      onChange('suku_bunga_flat', e.target.value === '' ? '' : e.target.value)
                    }
                    className="text-right focus:border-blue-500"
                  />
                  <span className="flex items-center">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Jenis Bunga / Biaya Jasa</Label>
                <CustomSelect
                  value={projection.interest_type || 'flat'}
                  onChange={v => onChange('interest_type', v)}
                  placeholder="Pilih Jenis Bunga"
                  width="w-full"
                  color="blue"
                  options={[
                    { value: 'flat', label: 'Flat' },
                    { value: 'effective', label: 'Efektif' },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Metode Angsuran */}
          <div className="space-y-2">
            <Label>Metode Angsuran</Label>
            <CustomSelect
              value={projection.repayment_method || 'flat'}
              onChange={v => onChange('repayment_method', v)}
              placeholder="Pilih Metode Angsuran"
              width="w-full md:w-1/2"
              color="blue"
              options={[
                { value: 'flat', label: 'Bulanan (Pokok dan Bunga)' },
                { value: 'annuity', label: 'Anuitas' },
                { value: 'effective', label: 'Efektif Menurun' },
              ]}
            />
          </div>

          {/* Asumsi Provisi */}
          <div>
            <h4 className="font-medium mb-3">Asumsi Provisi (Persentase atau Fixed Amount):</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Persentase Provisi</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={projection.beban_provisi_pct ?? ''}
                    onChange={e =>
                      onChange('beban_provisi_pct', e.target.value === '' ? '' : e.target.value)
                    }
                    className="text-right focus:border-blue-500"
                  />
                  <span className="flex items-center">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nominal Provisi (Fixed Amount)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                  <input
                    type="text"
                    value={formatCurrencyInput(calculated.bebanProvisi || 0)}
                    readOnly
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-right"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Asumsi Biaya Admin */}
          <div>
            <h4 className="font-medium mb-3">Asumsi Biaya Admin (Persentase atau Fixed Amount):</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Persentase Biaya Admin</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={projection.admin_fee_percentage ?? ''}
                    onChange={e =>
                      onChange('admin_fee_percentage', e.target.value === '' ? '' : e.target.value)
                    }
                    className="text-right focus:border-blue-500"
                  />
                  <span className="flex items-center">%</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Biaya Admin (Fixed Amount)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                  <input
                    type="text"
                    value={formatCurrencyInput(projection.beban_admin)}
                    onChange={e => handleCurrencyChange('beban_admin', e.target.value)}
                    placeholder="0"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 text-right"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Asumsi Biaya Lainnya */}
          <div>
            <h4 className="font-medium mb-3">Asumsi Biaya Lainnya:</h4>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Nominal Biaya Materai</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                  <input
                    type="text"
                    value={formatCurrencyInput(projection.biaya_materai)}
                    onChange={e => handleCurrencyChange('biaya_materai', e.target.value)}
                    placeholder="0"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 text-right"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nominal Biaya Notaris</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                  <input
                    type="text"
                    value={formatCurrencyInput(projection.biaya_notaris)}
                    onChange={e => handleCurrencyChange('biaya_notaris', e.target.value)}
                    placeholder="0"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 text-right"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nominal Biaya Asuransi</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                  <input
                    type="text"
                    value={formatCurrencyInput(projection.biaya_asuransi)}
                    onChange={e => handleCurrencyChange('biaya_asuransi', e.target.value)}
                    placeholder="0"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 text-right"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nominal Biaya Lainnya</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                  <input
                    type="text"
                    value={formatCurrencyInput(projection.biaya_lainnya)}
                    onChange={e => handleCurrencyChange('biaya_lainnya', e.target.value)}
                    placeholder="0"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 text-right"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
